import { z } from "zod";
import { CustomServiceFeeSchema } from "./serviceFee";

// Define interface for CartChildrenItemState for better type safety
export interface CartChildrenItemStateType {
    itemId?: string;
    childrenState?: (ChildStateItem | null)[];
}

interface ChildStateItem {
    itemId?: string;
    quantity?: number;
    childrenState?: ((CartChildrenItemStateType | null)[])[];
}

// CartChildrenItemState schema with recursive type handling
export const cartChildrenItemStateSchema: z.ZodType<CartChildrenItemStateType> = z.lazy(() =>
    z.object({
        itemId: z.string().optional(),
        childrenState: z.array(
            z.union([
                z.object({
                    itemId: z.string().optional(),
                    quantity: z.number().optional(),
                    childrenState: z.array(
                        z.array(
                            z.union([
                                cartChildrenItemStateSchema,
                                z.null()
                            ])
                        )
                    ).optional(),
                }),
                z.null()
            ])
        ).optional(),
    })
);

// CartItem schema
export const cartItemSchema = z.object({
    itemId: z.string().optional(),
    quantity: z.number().optional(),
    childrenState: z.array(
        z.union([
            cartChildrenItemStateSchema,
            z.null()
        ])
    ).optional(),
});

export type CartChildrenItemState = z.infer<typeof cartChildrenItemStateSchema>;
export type CartItem = z.infer<typeof cartItemSchema>;

// Cart schema
export const storeCartSchema = z.object({
    websiteId: z.string().optional(),
    storeId: z.string().optional(),
    lastUpdatedTS: z.number().optional(),
    storeName: z.string().optional(),
    storeAddress1: z.string().optional(),
    orderNote: z.string().max(510).optional(),
    scheduled_at_ts: z.number().optional(),
    usedCredit: z.number().optional(),
    editableServiceFeeRates: z.record(
        z.string(), //editable service fee id
        CustomServiceFeeSchema.pick({ ratePerOrder: true, feePerOrder: true })
    ).optional(),
    // items: z.array(cartItemSchema).optional() // causes circular deep shit
    // items: z.custom<CartItem[]>((val) => { // same issue: causes circular deep shit
    items: z.custom<any[]>((val) => {
        // Check if it's an array first
        if (!Array.isArray(val)) {
            return false;
        }

        // Validate each item against cartItemSchema
        return val.every(item => cartItemSchema.safeParse(item).success);

        // Validate each item against cartItemSchema and log detailed errors
        // for (let i = 0; i < val.length; i++) {
        //     const result = cartItemSchema.safeParse(val[i]);
        //     if (!result.success) {
        //         console.error(`Validation failed for item at index ${i}:`, result.error.format());
        //         console.error(`Item data:`, JSON.stringify(val[i], null, 2));
        //         return false;
        //     }
        // }
        // return true;

    }).optional(),
});
export type Cart = z.infer<typeof storeCartSchema>;

// Carts schema (a record of Cart objects)
export const storeCartsSchema = z.record(z.string(), storeCartSchema).nullable();
export type StoreCartsType = z.infer<typeof storeCartsSchema>;

// deliveryteam cart schema
export const deliveryteamOrderInfoSchema = storeCartSchema.omit({
    storeId: true,
    storeName: true,
    storeAddress1: true,
    items: true,
}).extend({
    deliveryteamId: z.string().optional(),
    deliveryteamName: z.string().optional(),
    deliveryteamAddress1: z.string().optional(),
});
// Carts schema (a record of Cart objects)
export type DeliveryteamOrderInfoType = z.infer<typeof deliveryteamOrderInfoSchema>;

export const deliveryteamOrderInfosSchema = z.record(z.string(), deliveryteamOrderInfoSchema).nullable();
export type DeliveryteamOrderInfosType = z.infer<typeof deliveryteamOrderInfosSchema>;


// type Cart = {
//     storeId?: string;
//     host?: string;
//     lastUpdatedTS?: number;
//     storeName?: string;
//     orderNote?: string;
//     items?: CartItem[];
// }
// export type Carts = Record<string, Cart>;

// const cartItem: CartItem = {
//     "itemId": "item-2",
//     "quantity": 1,
//     "childrenState": [
//         {
//             "itemId": "customization-3",
//             "childrenState": [
//                 {
//                     "itemId": "option-4",
//                     "quantity": 33,
//                     "childrenState": [
//                         [
//                             {
//                                 "itemId": "customization-2",
//                                 "childrenState": [
//                                     null
//                                 ]
//                             }
//                         ]
//                     ]
//                 }
//             ]
//         },
//         {
//             "itemId": "customization-5",
//             "childrenState": [
//                 {
//                     "itemId": "option-8",
//                     "quantity": 1,
//                     "childrenState": [
//                         [
//                             {
//                                 "itemId": "customization-6",
//                                 "childrenState": [
//                                     {
//                                         "itemId": "option-9",
//                                         "quantity": 1,
//                                         "childrenState": [
//                                             [
//                                                 {
//                                                     "itemId": "customization-7",
//                                                     "childrenState": [
//                                                         {
//                                                             "itemId": "option-10",
//                                                             "quantity": 2
//                                                         }
//                                                     ]
//                                                 }
//                                             ]
//                                         ]
//                                     }
//                                 ]
//                             }
//                         ]
//                     ]
//                 }
//             ]
//         },
//         {
//             "itemId": "customization-56",
//             "childrenState": [
//                 {
//                     "itemId": "option-211",
//                     "quantity": 1
//                 },
//                 {
//                     "itemId": "option-212",
//                     "quantity": 1
//                 },
//                 {
//                     "itemId": "option-213",
//                     "quantity": 1
//                 },
//                 {
//                     "itemId": "option-214",
//                     "quantity": 1
//                 },
//                 {
//                     "itemId": "option-215",
//                     "quantity": 1
//                 },
//                 {
//                     "itemId": "option-216",
//                     "quantity": 2
//                 },
//                 {
//                     "itemId": "option-217",
//                     "quantity": 1
//                 }
//             ]
//         }
//     ]
// }

// const item2: CartItem = {
//     "itemId": "item-1",
//     "quantity": 1,
//     "childrenState": [
//         {
//             "itemId": "customization-1",
//             "childrenState": [
//                 null,
//                 {
//                     "itemId": "option-3",
//                     "quantity": 1,
//                     "childrenState": [
//                         [
//                             {
//                                 "itemId": "customization-2",
//                                 "childrenState": [
//                                     {
//                                         "itemId": "option-2",
//                                         "quantity": 1
//                                     }
//                                 ]
//                             }
//                         ]
//                     ]
//                 }
//             ]
//         }
//     ]
// }

// console.log("cartItemSchema.safeParse(cartItem)");
// console.log(cartItemSchema.safeParse(cartItem));
// console.log("cartItemSchema.safeParse(item2)");
// console.log(cartItemSchema.safeParse(item2));